import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from 'redux/schemas';
import { AskNovoAiDrawerState } from 'institutions/components/ask-novoai/helpers';
import {
  AdditionalResource,
  LearnerFinalSummaryDetails,
  LearnerFinalSummaryDetailsResponse,
  HelpOptionsSettingsResponse,
  IHelpOptionsSettingsPayload,
  LearnerFinalSummaryStatus,
} from 'redux/schemas/models/help-options';
import { FileProps } from 'shared/components/nv-uploaded-file/nv-inline-uploaded-file';

export const translateHelpOptionsSettings = createAsyncThunk(
  'TRANSLATE_HELP_OPTIONS_SETTINGS',
  (catalogId: string, thunkAPI) => {
    const state = thunkAPI.getState() as RootState;
    const currentUser = state.models.users[state.app.currentUserId];
    const language = currentUser?.translationPreferenceLanguage;

    return axios
      .post<HelpOptionsSettingsResponse>(
      `/${catalogId}/translate_help_resources.json`,
      { language },
    )
      .then((response) => response.data.result)
      .catch(() => ({}));
  },
);
interface GetHelpOptionsSettingsPayload {
  catalogId: string;
  editMode?: boolean;
}

export const getHelpOptionsSettings = createAsyncThunk(
  'GET_HELP_OPTIONS_SETTINGS',
  ({ catalogId, editMode }: GetHelpOptionsSettingsPayload) => axios
    .get<HelpOptionsSettingsResponse>(
    `/${catalogId}/learning_assistants.json`,
    { params: editMode ? { edit_mode: true } : {} },
  )
    .then((response) => response.data.result)
    .catch(() => ({})),
);

interface SaveHelpOptionsSettingsPayload {
  catalogId: string;
  payload: IHelpOptionsSettingsPayload;
}

interface SavePersonalizedTakeawaysPayload {
  catalogId: string;
  enabled: boolean;
}

interface GenerateLearnerFinalSummaryPayload {
  catalogId: string;
  userId: number;
}

interface GetLearnerFinalSummaryDetailsPayload {
  catalogId: string;
  userId: number;
  learnerFinalSummaryReportId: number;
}

export interface LearnerFinalSummaryUpdatedPayload {
  learnerFinalSummaryReportId: number;
  userId: number;
  status: LearnerFinalSummaryStatus;
  available: boolean;
  generatedAt: string;
  errorMessage?: string;
}

export const saveHelpOptionsSettings = createAsyncThunk(
  'SAVE_HELP_OPTIONS_SETTINGS',
  async ({ catalogId, payload }: SaveHelpOptionsSettingsPayload, thunkAPI) => {
    const response = await axios
      .post(`/${catalogId}/learning_assistants.json`, payload)
      .then((res) => res.data.result);

    const state = thunkAPI.getState() as RootState;
    const currentUser = state.models.users[state.app.currentUserId];
    const institution = state.models.institutions[state.app.currentInstitutionId];
    const course = state.models.courses[state.app.currentCatalogId];

    if (
      currentUser?.translationPreferenceLanguage
      && institution?.automatedTranslationEnabled
      && course?.automatedTranslationEnabled
    ) {
      await thunkAPI.dispatch(translateHelpOptionsSettings(catalogId));
    }

    thunkAPI.dispatch(getHelpOptionsSettings({ catalogId }));
    thunkAPI.dispatch(resetState());
    return response;
  },
);

export const savePersonalizedTakeawaysSettings = createAsyncThunk(
  'SAVE_PERSONALIZED_TAKEAWAYS_SETTINGS',
  async ({ catalogId, enabled }: SavePersonalizedTakeawaysPayload) => {
    const response = await axios
      .post(`/${catalogId}/learning_assistants.json`, {
        hasPersonalizedTakeawaysEnabled: enabled,
      })
      .then((res) => res.data.result);

    return response;
  },
);

export const generateLearnerFinalSummary = createAsyncThunk(
  'GENERATE_LEARNER_FINAL_SUMMARY',
  async ({ catalogId, userId }: GenerateLearnerFinalSummaryPayload) => {
    const response = await axios
      .post(`/${catalogId}/learning_assistants/final_summaries.json`, {
        userId,
      })
      .then((res) => res.data.result);
    return response;
  },
);

export const getLearnerFinalSummaryDetails = createAsyncThunk<
LearnerFinalSummaryDetails,
GetLearnerFinalSummaryDetailsPayload
>(
  'GET_LEARNER_FINAL_SUMMARY_DETAILS',
  async ({
    catalogId,
    learnerFinalSummaryReportId,
  }, { rejectWithValue }) => {
    try {
      const response = await axios.get<LearnerFinalSummaryDetailsResponse>(
        `/${catalogId}/learning_assistants/final_summaries/${learnerFinalSummaryReportId}.json`,
      );
      return response.data.result;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setLearnerFinalSummaryUpdated = createAction(
  'SET_LEARNER_FINAL_SUMMARY_UPDATED',
  (payload: LearnerFinalSummaryUpdatedPayload) => ({
    payload,
  }),
);

export interface RecentQuestionsResponse {
  questionText: string;
  id: number;
}

export const getRecentQuestions = createAsyncThunk(
  'GET_RECENT_QUESTIONS',
  (catalogId: string) => axios
    .get<{ result: RecentQuestionsResponse[] }>(
    `/${catalogId}/user_qnas.json`,
  )
    .then((response) => response.data.result)
    .catch(() => []),
);

export interface AskNovoAiStreamRequestPayload {
  streamId?: string;
  qnaId?: number;
  courseId: number;
  userId: number;
  userQuery?: string;
  lecturePageIds?: number[];
  fileIds?: number[];
  actionBaseUrl: string;
  persistenceCallbackUrl: string;
  dataEnv: string;
  responseLanguage?: string;
}

export interface AskNovoAiStreamBootstrapResponse extends AskNovoAiStreamRequestPayload {
  jwtToken: string;
  expiresAt: string;
  streamId: string;
  aiApiEndpoint: string;
}
interface LiveEventsResponse {
  lecturePageId: number;
  id: number;
  title: string;
  startTime: string;
  endTime: string;
}

interface ReferencedComponentsResponse {
  id: number;
  type: string;
  index: number;
  lecturePage: {
    id: number;
    title: string;
  };
}

interface ExternalReferenceResponse {
  title: string;
  url: string;
}

export interface AskNovoAiQuestionResponse {
  id?: number;
  questionText: string;
  answer: string;
  certificateUrl?: string;
  responseCode: number;
  liveEvents: LiveEventsResponse[];
  referencedComponents: ReferencedComponentsResponse[];
  externalReferences?: ExternalReferenceResponse[];
}

export const startAskNovoAiStreamSession = createAsyncThunk(
  'START_ASK_NOVO_AI_STREAM_SESSION',
  async (catalogId: string) => {
    const response = await axios
      .post<AskNovoAiStreamBootstrapResponse>(
      `/${catalogId}/user_qnas/bootstrap_stream.json`,
    )
      .then((res) => res.data);
    return response;
  },
);

export const setAskNovoAiResponse = createAction(
  'SET_ASK_NOVO_AI_RESPONSE',
  (response: AskNovoAiQuestionResponse) => ({
    payload: response,
  }),
);
export enum VoteTypes {
  UPVOTE = 'vote_up',
  DOWNVOTE = 'vote_down',
}
interface VoteNovoAiAnswerPayload {
  catalogId: string;
  qnaId: number;
  vote: VoteTypes;
}

export const voteNovoAiAnswer = createAsyncThunk(
  'VOTE_NOVO_AI',
  async ({ catalogId, qnaId, vote }: VoteNovoAiAnswerPayload) => {
    const response = await axios
      .post(`/${catalogId}/user_qnas/${qnaId}/${vote}.json`)
      .then((res) => res.data);
    return response;
  },
);

export const openNovoAIDialog = createAction('HELP_OPTIONS_OPEN_NOVOAI');
export const closeNovoAIDialog = createAction('HELP_OPTIONS_CLOSE_NOVOAI');
export const changeDrawerState = createAction(
  'CHANGE_NOVOAI_DRAWER_STATE',
  (state: AskNovoAiDrawerState) => ({
    payload: state,
  }),
);

// Vote state actions
export const setVoteLoading = createAction(
  'SET_VOTE_LOADING',
  (questionId: number, isLoading: boolean) => ({
    payload: { questionId, isLoading },
  }),
);

export const setVoteState = createAction(
  'SET_VOTE_STATE',
  (questionId: number, voteType: VoteTypes, voted: boolean) => ({
    payload: { questionId, voteType, voted },
  }),
);

export const showVoteFeedback = createAction(
  'SHOW_VOTE_FEEDBACK',
  (questionId: number, show: boolean) => ({
    payload: { questionId, show },
  }),
);

export const resetVoteState = createAction(
  'RESET_VOTE_STATE',
  (questionId: number) => ({
    payload: { questionId },
  }),
);

export const resetState = createAsyncThunk(
  'RESET_HELP_OPTIONS_STATE',
  async () => ({}),
);

export const resetAskNovoAiUiState = createAction('RESET_ASK_NOVO_AI_UI_STATE');

export interface UploadAdditionalResourceResponse {
  fileName: string;
  url: string;
  uniqueId: string;
  fileSize: number;
  contentType: string;
  resourceId: number;
  valid: boolean;
  errors?: string;
}

export interface UploadAdditionalResourcesResponse {
  result: UploadAdditionalResourceResponse[];
  message: string;
}

interface UploadAdditionalResourcesPayload {
  catalogId: string;
  additionalResources: FileProps[];
}

export const uploadAdditionalResources = createAsyncThunk(
  'UPLOAD_ADDITIONAL_RESOURCES',
  async ({ catalogId, additionalResources }: UploadAdditionalResourcesPayload) => {
    const response = await axios
      .post<UploadAdditionalResourcesResponse>(`/${catalogId}/learning_assistant_additional_resources/upload.json`, {
      additional_resources: additionalResources,
    })
      .then((res) => res.data.result);
    return response;
  },
);

export const addAdditionalResourceFiles = createAction(
  'ADD_ADDITIONAL_RESOURCE_FILES',
  (files: File[]) => ({
    payload: { files },
  }),
);

export const updateAdditionalResource = createAction(
  'UPDATE_ADDITIONAL_RESOURCE',
  (uniqueId: string, updates: Partial<AdditionalResource>) => ({
    payload: { uniqueId, updates },
  }),
);

export const removeAdditionalResource = createAction(
  'REMOVE_ADDITIONAL_RESOURCE',
  (id: number) => ({
    payload: { id },
  }),
);
